import {i18n} from '@online-offer/i18n';
import {Separator} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {Fragment} from 'react';

import {isEmpty, isNil} from 'ramda';

import {Nullish} from 'shared/web';

import {FinancialProductOffer} from '../../api/types/onlineOfferTypes';
import {CardOo} from '../../components/CardOo/CardOo';
import {SectionId} from '../../consts/Sections';
import {useFinancialProducts} from '../../hooks/useFinancialProducts';
import {requestOpenFinancialProduct} from '../FinancialProducts/financialProductNavigation';
import {formatFinancialProductMoney} from '../FinancialProducts/formatFinancialProductMoney';
import {FinancialProductSummaryRow} from './FinancialProductSummaryRow';

const FINANCIAL_PRODUCTS_SECTION_ID: SectionId = 'FINANCIAL_PRODUCTS';

const toMonthlyPayment = (offer: FinancialProductOffer | Nullish) =>
  offer?.availability === 'AVAILABLE' ? offer.calculation?.monthlyPayment : undefined;

/** Standard and balloon share the cheapest financing card; leasing remains separate. */
export function FinancialProductSummary() {
  const {financialProducts} = useFinancialProducts();
  const formatCurrency = useFormatCurrency();

  const financing = [financialProducts?.standard, financialProducts?.balloon]
    .sort(
      (left, right) =>
        Number(toMonthlyPayment(left)?.amount ?? Infinity) -
        Number(toMonthlyPayment(right)?.amount ?? Infinity)
    )
    .at(0);

  const quotes = [
    {
      id: 'financing',
      label: i18n.t('financialProducts.summary.financingFrom'),
      monthlyPayment: toMonthlyPayment(financing),
      productType: financing?.productType,
    },
    {
      id: 'leasing',
      label: i18n.t('financialProducts.summary.leasingFrom'),
      monthlyPayment: toMonthlyPayment(financialProducts?.leasing),
      productType: 'LEASING' as const,
    },
  ].flatMap((quote) =>
    isNil(quote.monthlyPayment) || isNil(quote.productType)
      ? []
      : [{...quote, monthlyPayment: quote.monthlyPayment, productType: quote.productType}]
  );

  if (isEmpty(quotes)) {
    return null;
  }

  return (
    <CardOo backgroundColor="semantic.bg.subtle">
      <VStack spacing={5}>
        {quotes.map((quote, index) => (
          <Fragment key={quote.id}>
            <Show when={index > 0}>
              <Separator spacing={0} />
            </Show>
            <FinancialProductSummaryRow
              id={quote.id}
              label={quote.label}
              amount={formatFinancialProductMoney(quote.monthlyPayment, formatCurrency)}
              onCalculate={() => {
                requestOpenFinancialProduct(quote.productType);
                document
                  .getElementById(FINANCIAL_PRODUCTS_SECTION_ID)
                  ?.scrollIntoView({behavior: 'smooth', block: 'start'});
              }}
            />
          </Fragment>
        ))}
      </VStack>
    </CardOo>
  );
}
